import { giphyBlack, giphyIndigo, giphyLightGrey, giphyRed } from '@giphy/js-brand'
import React, { MouseEvent, useContext, useState } from 'react'
import MessageContext from 'shared/components/context/message'
import UserContext from 'shared/components/context/user'
import DotsLoader from 'shared/components/loaders/dots'
import { googleAnalyticsEvent } from 'shared/util/analytics'
import styled from 'styled-components'
import AlertModal from '../../alert-modal'
import { PillBlock } from '../../blocks'
import DeleteModal from '../modals/delete-modal'
import { ICapture, IPending } from '../shared/types'
import API from '../shared/utils/api'
import { CompletedCaptureList, PendingCaptureList } from './capture-list'

const api = new API()

const Placeholder = styled.div`
    margin-top: 30px;
    height: 400px;
    background-image: url('/static/img/supercut/placeholder-capture-table.svg');
    position: relative;

    &:after {
        content: '';
        background: linear-gradient(0deg, ${giphyBlack} 0%, rgba(0, 0, 0, 0) 100%);
        position: absolute;
        height: 100px;
        right: 0;
        bottom: 0;
        left: 0;
    }
`

const Heading = styled.div`
    letter-spacing: 0.26px;
    font-weight: bold;
    margin-top: 30px;
    margin-bottom: 15px;
`

const NotificationInfo = styled.span`
    float: right;
    color: ${giphyLightGrey};
    font-style: italic;
    font-size: 13px;
`

const Pill = styled(PillBlock)`
    background: ${giphyIndigo};
    margin-left: 5px;
`

type Props = {
    captures: ICapture[]
    pending: IPending[]
    isAllCaptures: boolean
    onCaptureDeleted?: (id: string) => void
}

const CaptureTable = ({ captures, pending, isAllCaptures, onCaptureDeleted = () => {} }: Props) => {
    const [deleteConfirming, setDeleteConfirming] = useState<ICapture>()
    const [deleteLoading, setDeleteLoading] = useState(false)
    const [pendingDeletionID, setPendingDeletionID] = useState('')
    const { sendMessage } = useContext(MessageContext)
    const { user } = useContext(UserContext)

    const deleteClicked = (evt: MouseEvent, capture: ICapture) => {
        evt.preventDefault()
        setDeleteConfirming(capture)
        setPendingDeletionID(capture.id)
    }

    const confirmDelete = async () => {
        googleAnalyticsEvent({
            category: 'Super Cut',
            action: 'Delete Capture',
            label: pendingDeletionID,
        })
        if (deleteConfirming) {
            setDeleteLoading(true)
            await api.deleteCapture(deleteConfirming!.id)
            setDeleteLoading(false)
            setDeleteConfirming(undefined)
            sendMessage('Capture Deleted!')
            onCaptureDeleted(pendingDeletionID)
        }
    }

    const cancelDelete = () => {
        setDeleteConfirming(undefined)
    }

    if (captures.length === 0 && pending.length === 0) {
        return <Placeholder />
    } else {
        return (
            <div>
                {deleteConfirming !== undefined && (
                    <DeleteModal onClickOutside={cancelDelete}>
                        {deleteLoading ? (
                            <DotsLoader isAbsolute={true} />
                        ) : (
                            <AlertModal
                                {...{
                                    title: `Are you sure you want to delete this capture?`,
                                    stickerId: 'jOsoGmmWGSloPU8fMH',
                                    primaryButtonColor: giphyRed,
                                    primaryButtonLabel: `Delete`,
                                    secondaryButtonLabel: `Cancel`,
                                    onPrimaryButtonClick: confirmDelete,
                                    onSecondaryButtonClick: cancelDelete,
                                }}
                            />
                        )}
                    </DeleteModal>
                )}

                {pending.length > 0 && (
                    <Heading>
                        Pending Captures <Pill>{pending.length >= 10 ? '10+' : pending.length}</Pill>
                        <NotificationInfo>
                            {
                                "If you've enabled Browser Notifications, you'll receive an update when processing is completed."
                            }
                        </NotificationInfo>
                    </Heading>
                )}

                <PendingCaptureList captures={pending} />

                {captures.length > 0 && (
                    <Heading>
                        Captures <Pill>{captures.length >= 50 ? '50+' : captures.length}</Pill>
                    </Heading>
                )}

                <CompletedCaptureList
                    captures={captures}
                    showUsername={user.isStaff && isAllCaptures}
                    onDeleteClicked={deleteClicked}
                />
            </div>
        )
    }
}

export default CaptureTable
